import { useState, useEffect, useRef } from 'react'
import { useWizardStore } from '../store/wizardStore'
import { Layout, WizardHeader } from './ui/Layout'
import { Card, CardContent } from './ui/Card'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import Step1UserDetails from './steps/Step1UserDetails'
import Step2GiftDecision from './steps/Step2GiftDecision'
import Step3PickupLocation from './steps/Step3PickupLocation'
import Step4GiftBuilder from './steps/Step4GiftBuilder'
import Step5SlotSelection from './steps/Step5SlotSelection'
import Step6SantaDetails from './steps/Step6SantaDetails'
import Step7TokenPurchase from './steps/Step7TokenPurchase'
import Step8Payment from './steps/Step8Payment'
import { api } from '../lib/api'

// "Checkout within X minutes" banner. Reads the hold expiry the server returned; on zero the
// store drops the slot and returns the customer to the slot picker.
function SlotCountdown() {
  const { slotHeldUntil, expireSlot, currentStep } = useWizardStore()
  const [now, setNow] = useState(Date.now())
  // The hold we've already warned about, so the modal shows once per hold (a refreshed hold can warn again)
  const [warnedFor, setWarnedFor] = useState<string | null>(null)
  const [showWarning, setShowWarning] = useState(false)

  useEffect(() => {
    if (!slotHeldUntil) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [slotHeldUntil])

  const remaining = slotHeldUntil ? new Date(slotHeldUntil).getTime() - now : 0

  useEffect(() => {
    if (slotHeldUntil && remaining <= 0) expireSlot()
  }, [slotHeldUntil, remaining, expireSlot])

  const low = remaining < 5 * 60000

  useEffect(() => {
    // Not on the payment step: a modal there would interrupt card entry, so the banner alone warns
    if (slotHeldUntil && remaining > 0 && low && warnedFor !== slotHeldUntil && currentStep !== 8) {
      setWarnedFor(slotHeldUntil)
      setShowWarning(true)
    }
    if (!slotHeldUntil || remaining <= 0) setShowWarning(false)
  }, [slotHeldUntil, remaining, low, warnedFor, currentStep])

  if (!slotHeldUntil || remaining <= 0) return null
  const m = Math.floor(remaining / 60000)
  const sec = Math.floor((remaining % 60000) / 1000)
  const clock = `${m}:${String(sec).padStart(2, '0')}`
  return (
    <>
    <Modal
      isOpen={showWarning}
      onClose={() => setShowWarning(false)}
      title="Your Santa slot is about to be released"
      icon="warning"
      actions={
        <Button onClick={() => setShowWarning(false)} fullWidth autoFocus>
          Continue my order
        </Button>
      }
    >
      <p>
        We're holding your slot for <span className="font-mono font-semibold text-gray-900">{clock}</span> more.
        {' '}Finish your order and pay before then, or the slot will be released for another family.
      </p>
    </Modal>
    <div className={`mb-4 rounded-lg border px-4 py-2 text-center text-sm ${low ? 'border-red-300 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
      Your Santa slot is reserved for <span className="font-mono font-semibold">{m}:{String(sec).padStart(2, '0')}</span>. Complete checkout before it expires.
      {/* ponytail: announce once at the 5 minute mark, not every tick */}
      <p role="status" className="sr-only">{low ? 'Less than 5 minutes left to complete checkout.' : ''}</p>
    </div>
    </>
  )
}

export default function Wizard() {
  const { currentStep, wantsGifts, wantsSanta, wantsTokensOnly } = useWizardStore()
  const [acceptOrders, setAcceptOrders] = useState(true)
  const [loading, setLoading] = useState(true)

  // Steps this parent will actually see, so "Step N of M" doesn't jump when steps are skipped
  const path = wantsTokensOnly
    ? [1, 2, 7, 8]
    : [1, 2, ...(wantsGifts ? [3, 4] : []), ...(wantsSanta ? [5, 6] : []), 7, 8]
  const stepIndex = path.indexOf(currentStep)

  // On step change: back to the top and focus the new step's title so screen readers announce it
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    window.scrollTo(0, 0)
    document.querySelector<HTMLElement>('main h2')?.focus()
  }, [currentStep])

  useEffect(() => {
    checkAcceptOrders()
  }, [])

  async function checkAcceptOrders() {
    try {
      const result = await api.getAcceptOrdersSetting()
      setAcceptOrders(result.acceptOrders)
    } catch (error) {
      console.error('Failed to check acceptOrders setting:', error)
      // Default to true if there's an error
      setAcceptOrders(true)
    } finally {
      setLoading(false)
    }
  }

  // Show loading state while checking setting
  if (loading) {
    return (
      <Layout>
        <WizardHeader title="BPAC - Elves' Workshop" />
        <Card variant="elevated">
          <CardContent>
            <div className="text-center py-12">
              <p role="status" className="text-gray-600">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </Layout>
    )
  }

  // Show closed message if orders are not being accepted
  if (!acceptOrders) {
    return (
      <Layout>
        <WizardHeader title="BPAC - Elves' Workshop" />
        <Card variant="elevated">
          <CardContent>
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Santa is sleeping. See you next year!</h2>
              <p className="text-gray-600 mb-6">The Elves' Workshop is currently closed.</p>
              <a
                href="https://bpac.sumupstore.com/product/xmas-fayre-50p-token"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-green-700 hover:bg-green-800 text-white font-semibold rounded-lg transition-colors"
              >
                Buy Tokens Online
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </div>
          </CardContent>
        </Card>
      </Layout>
    )
  }

  // Calculate dynamic steps based on user choices
  const getStepComponent = () => {
    // If user doesn't want gifts, skip gift-related steps
    if (!wantsGifts && currentStep >= 3 && currentStep <= 4) {
      return <Step5SlotSelection />
    }

    switch (currentStep) {
      case 1:
        return <Step1UserDetails />
      case 2:
        return <Step2GiftDecision />
      case 3:
        return <Step3PickupLocation />
      case 4:
        return <Step4GiftBuilder />
      case 5:
        return <Step5SlotSelection />
      case 6:
        return <Step6SantaDetails />
      case 7:
        return <Step7TokenPurchase />
      case 8:
        return <Step8Payment />
      default:
        return <Step1UserDetails />
    }
  }

  return (
    <Layout>
      <WizardHeader
        title="BPAC - Elves' Workshop"
        step={stepIndex >= 0 ? stepIndex + 1 : currentStep}
        totalSteps={stepIndex >= 0 ? path.length : 8}
      />
      <SlotCountdown />
      <Card variant="elevated">
        {getStepComponent()}
      </Card>
    </Layout>
  )
}
